import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FinetuneDataset } from "../../model_gen/finetuning";
import { Button } from "../ui/button";
import { MoreHorizontalIcon } from "lucide-react";

import moment from "moment";
import PaginatedTable, { PaginatedTableColumn } from "../ui/paginated-table";

interface DatasetsTableProps {
  datasets: FinetuneDataset[];
  loading: boolean;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onDeleteDataset?: (dataset: FinetuneDataset) => void;
  emptyTitle?: string;
  emptyDescription?: string;
}

const DatasetsTable: React.FC<DatasetsTableProps> = ({
  datasets,
  loading,
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onDeleteDataset,
  emptyTitle = "No datasets found",
  emptyDescription = "Create a new dataset to get started with model fine-tuning.",
}) => {
  const navigate = useNavigate();
  const [deletingDatasetId, setDeletingDatasetId] = useState<number | null>(
    null
  );

  const formatDate = (timestamp: number) => {
    if (timestamp <= 0) return "Never";
    const date = moment(timestamp);
    return date.format("MMM D, YYYY");
  };

  const formatTime = (timestamp: number) => {
    if (timestamp <= 0) return "";
    const date = moment(timestamp);
    return date.format("h:mm A");
  };

  const columns: PaginatedTableColumn<FinetuneDataset>[] = [
    {
      key: "name",
      header: "Name",
      width: "w-[25%]",
      sortable: false,
      render: (dataset) => (
        <div className="text-sm font-gc-reg text-foreground hover:text-maitai-green transition-colors">
          {dataset.name}
        </div>
      ),
    },
    {
      key: "major_version",
      header: "Version",
      width: "w-[10%]",
      sortable: false,
      render: (dataset) => (
        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400">
          v{dataset.major_version || 1}.{dataset.minor_version || 0}
        </span>
      ),
    },
    {
      key: "date_created",
      header: "Date Created",
      width: "w-[20%]",
      sortable: false,
      render: (dataset) =>
        dataset.date_created ? (
          <div className="space-y-1">
            <div className="text-sm font-gc-reg text-foreground">
              {formatDate(dataset.date_created)}
            </div>
            <div className="text-xs text-muted-foreground">
              {formatTime(dataset.date_created)}
            </div>
          </div>
        ) : (
          <span className="text-sm text-muted-foreground">-</span>
        ),
    },
    {
      key: "intent_group_name",
      header: "Source",
      width: "w-[25%]",
      sortable: false,
      render: (dataset) => (
        <div className="text-sm font-gc-reg text-foreground">
          {dataset.intent_group_name ||
            (dataset.application_name && dataset.intent_name
              ? `${dataset.application_name} / ${dataset.intent_name}`
              : "-")}
        </div>
      ),
    },
    {
      key: "request_count",
      header: "Requests",
      width: "w-[10%]",
      sortable: false,
      render: (dataset) => (
        <span className="text-sm font-gc-med text-foreground">
          {dataset.request_count ? dataset.request_count.toLocaleString() : "0"}
        </span>
      ),
    },
    {
      key: "id",
      header: "",
      width: "w-[10%]",
      sortable: false,
      render: (dataset) => (
        <div className="relative">
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              // Simple dropdown menu using native browser context menu
              const menu = document.createElement("div");
              menu.className =
                "absolute right-0 top-8 z-50 min-w-48 bg-popover border border-border rounded-md shadow-lg py-1";

              // Create delete button
              const deleteButton = document.createElement("button");
              const isDeleting = deletingDatasetId === dataset.id;
              deleteButton.className = `flex items-center w-full px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors ${
                isDeleting
                  ? "text-muted-foreground cursor-not-allowed"
                  : "text-red-600"
              }`;
              deleteButton.disabled = isDeleting;
              deleteButton.innerHTML = isDeleting
                ? `
                <svg class="w-4 h-4 mr-2 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                </svg>
                Deleting...
              `
                : `
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                </svg>
                Delete
              `;
              deleteButton.onclick = (evt) => {
                evt.stopPropagation();

                // Don't proceed if already deleting
                if (isDeleting) {
                  return;
                }

                menu.remove();

                // Set loading state and call onDeleteDataset
                if (onDeleteDataset) {
                  setDeletingDatasetId(dataset.id);
                  onDeleteDataset(dataset);
                  // Note: The parent component will handle success/error,
                  // but we'll clear loading state after a delay to show feedback
                  setTimeout(() => setDeletingDatasetId(null), 1000);
                }
              };
              menu.appendChild(deleteButton);

              // Position and show menu
              const container = e.currentTarget.parentElement;
              if (container) {
                container.appendChild(menu);

                // Remove menu when clicking outside
                const handleClickOutside = (evt: Event) => {
                  if (!menu.contains(evt.target as Node)) {
                    menu.remove();
                    document.removeEventListener("click", handleClickOutside);
                  }
                };
                setTimeout(
                  () => document.addEventListener("click", handleClickOutside),
                  0
                );
              }
            }}>
            <MoreHorizontalIcon className="w-4 h-4" />
          </Button>
        </div>
      ),
    },
  ];

  const handleRowClick = (dataset: FinetuneDataset) => {
    navigate(`/forge/datasets/${dataset.id}`);
  };

  return (
    <PaginatedTable
      data={datasets}
      columns={columns}
      loading={loading}
      currentPage={currentPage}
      onPageChange={onPageChange}
      pageSize={itemsPerPage}
      totalItems={totalItems}
      serverSidePagination={true}
      emptyTitle={emptyTitle}
      emptyDescription={emptyDescription}
      onRowClick={handleRowClick}
      rowClassName="hover:bg-accent/20 transition-colors"
      showPagination={true}
    />
  );
};

export default DatasetsTable;
